import React, { useState, useEffect } from 'react';
import { FaUsers, FaChartBar, FaDollarSign, FaPercentage, FaSpinner, FaArrowUp, FaArrowDown } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TeamsAnalytics = () => {
  const [teams, setTeams] = useState([]);
  const [teamsAnalytics, setTeamsAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('total_leads');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchTeamsAndAnalytics();
  }, []);

  const fetchTeamsAndAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('accessToken');
      
      // Fetch teams
      const teamsResponse = await fetch(`${API_URL}/api/teams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!teamsResponse.ok) {
        throw new Error('Failed to fetch teams');
      }

      const teamsData = await teamsResponse.json();
      setTeams(teamsData.teams || []);

      // Fetch analytics for each team
      const analyticsPromises = teamsData.teams.map(async (team) => {
        try {
          const analyticsResponse = await fetch(`${API_URL}/api/teams/${team._id}/analytics`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (analyticsResponse.ok) {
            const analyticsData = await analyticsResponse.json();
            return {
              ...team,
              analytics: analyticsData.analytics
            };
          } else {
            return {
              ...team,
              analytics: {
                total_leads: 0,
                total_deals: 0,
                total_revenue: 0,
                conversion_rate: 0,
                agent_performance: []
              }
            };
          }
        } catch (error) {
          console.error(`Error fetching analytics for team ${team.team_name}:`, error);
          return {
            ...team,
            analytics: {
              total_leads: 0,
              total_deals: 0,
              total_revenue: 0,
              conversion_rate: 0,
              agent_performance: []
            }
          };
        }
      });

      const analyticsResults = await Promise.all(analyticsPromises);
      setTeamsAnalytics(analyticsResults);
      
    } catch (error) {
      console.error('Error fetching teams and analytics:', error);
      setError('Failed to load teams data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedTeams = [...teamsAnalytics].sort((a, b) => {
    let aValue, bValue;
    
    if (sortBy === 'team_name') {
      aValue = a.team_name;
      bValue = b.team_name;
    } else if (sortBy === 'manager_name') {
      aValue = a.manager_name;
      bValue = b.manager_name;
    } else if (sortBy === 'agent_count') {
      aValue = a.sales_agents?.length || 0;
      bValue = b.sales_agents?.length || 0;
    } else {
      aValue = a.analytics?.[sortBy] || 0;
      bValue = b.analytics?.[sortBy] || 0;
    }

    if (typeof aValue === 'string') {
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <FaArrowUp /> : <FaArrowDown />;
  };

  if (loading) {
    return (
      <div className="teams-analytics-loading">
        <FaSpinner className="spinner" />
        <p>Loading teams analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teams-analytics-error">
        <p>{error}</p>
        <button onClick={fetchTeamsAndAnalytics} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (teamsAnalytics.length === 0) {
    return (
      <div className="no-teams-analytics">
        <FaUsers size={48} />
        <h3>No Teams Found</h3>
        <p>Create teams in the Settings page to see analytics here.</p>
      </div>
    );
  }

  return (
    <div className="teams-analytics">
      <div className="teams-analytics-header">
        <h2><FaUsers /> Teams Performance Comparison</h2>
        <p>Compare performance metrics across all teams</p>
      </div>

      <div className="teams-analytics-table-container">
        <table className="teams-analytics-table">
          <thead>
            <tr>
              <th 
                onClick={() => handleSort('team_name')}
                className={`sortable ${sortBy === 'team_name' ? 'active' : ''}`}
              >
                Team Name {getSortIcon('team_name')}
              </th>
              <th 
                onClick={() => handleSort('manager_name')}
                className={`sortable ${sortBy === 'manager_name' ? 'active' : ''}`}
              >
                Manager {getSortIcon('manager_name')}
              </th>
              <th 
                onClick={() => handleSort('agent_count')}
                className={`sortable ${sortBy === 'agent_count' ? 'active' : ''}`}
              >
                Agents {getSortIcon('agent_count')}
              </th>
              <th 
                onClick={() => handleSort('total_leads')}
                className={`sortable ${sortBy === 'total_leads' ? 'active' : ''}`}
              >
                <FaChartBar /> Total Leads {getSortIcon('total_leads')}
              </th>
              <th 
                onClick={() => handleSort('total_deals')}
                className={`sortable ${sortBy === 'total_deals' ? 'active' : ''}`}
              >
                <FaChartBar /> Deals Closed {getSortIcon('total_deals')}
              </th>
              <th 
                onClick={() => handleSort('total_revenue')}
                className={`sortable ${sortBy === 'total_revenue' ? 'active' : ''}`}
              >
                <FaDollarSign /> Revenue {getSortIcon('total_revenue')}
              </th>
              <th 
                onClick={() => handleSort('conversion_rate')}
                className={`sortable ${sortBy === 'conversion_rate' ? 'active' : ''}`}
              >
                <FaPercentage /> Conversion Rate {getSortIcon('conversion_rate')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team) => (
              <tr key={team._id} className="team-row">
                <td className="team-name-cell">
                  <div className="team-name">
                    <strong>{team.team_name}</strong>
                  </div>
                </td>
                <td className="manager-cell">
                  {team.manager_name}
                </td>
                <td className="agent-count-cell">
                  <span className="agent-count-badge">
                    {team.sales_agents?.length || 0}
                  </span>
                </td>
                <td className="metric-cell">
                  <span className="metric-value">
                    {team.analytics?.total_leads?.toLocaleString() || 0}
                  </span>
                </td>
                <td className="metric-cell">
                  <span className="metric-value">
                    {team.analytics?.total_deals?.toLocaleString() || 0}
                  </span>
                </td>
                <td className="metric-cell">
                  <span className="metric-value">
                    {formatCurrency(team.analytics?.total_revenue || 0)}
                  </span>
                </td>
                <td className="metric-cell">
                  <span className={`conversion-rate ${
                    (team.analytics?.conversion_rate || 0) >= 10 ? 'high' : 
                    (team.analytics?.conversion_rate || 0) >= 5 ? 'medium' : 'low'
                  }`}>
                    {(team.analytics?.conversion_rate || 0).toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="teams-summary">
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">
              <FaUsers />
            </div>
            <div className="summary-content">
              <h4>Total Teams</h4>
              <p className="summary-value">{teamsAnalytics.length}</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <FaChartBar />
            </div>
            <div className="summary-content">
              <h4>Total Agents</h4>
              <p className="summary-value">
                {teamsAnalytics.reduce((sum, team) => sum + (team.sales_agents?.length || 0), 0)}
              </p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <FaChartBar />
            </div>
            <div className="summary-content">
              <h4>Combined Leads</h4>
              <p className="summary-value">
                {teamsAnalytics.reduce((sum, team) => sum + (team.analytics?.total_leads || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">
              <FaDollarSign />
            </div>
            <div className="summary-content">
              <h4>Combined Revenue</h4>
              <p className="summary-value">
                {formatCurrency(teamsAnalytics.reduce((sum, team) => sum + (team.analytics?.total_revenue || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamsAnalytics; 